"use client"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Label,
  cn,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { Save } from 'lucide-react'
import { useBriefs, useSaveCandidate } from '../../hooks/use-briefs'
import { toast } from 'sonner'

export type SaveToBriefControlProps = {
  profileId: string
  profileName?: string
  size?: 'sm' | 'default'
  variant?: 'outline' | 'ghost'
  className?: string
  /** Stop click propagation when nested in clickable cards */
  stopPropagation?: boolean
}

export function SaveToBriefControl({
  profileId,
  profileName,
  size = 'sm',
  variant = 'outline',
  className,
  stopPropagation = true,
}: SaveToBriefControlProps) {
  const [open, setOpen] = useState(false)
  const [selectedBriefId, setSelectedBriefId] = useState('')
  const [notes, setNotes] = useState('')
  const { briefs } = useBriefs()
  const { saveCandidate, isSaving } = useSaveCandidate()

  const handleSave = () => {
    if (!selectedBriefId) {
      toast.error('Please select a brief')
      return
    }

    saveCandidate(
      {
        briefId: selectedBriefId,
        data: {
          profile_id: profileId,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: (result) => {
          toast.success(`Saved to brief: ${result.brief_title}`)
          setOpen(false)
          setNotes('')
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to save to brief')
        },
      },
    )
  }

  const label = profileName ? `Save ${profileName} to brief` : 'Save to brief'

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size={size}
          variant={variant}
          className={cn('gap-1.5', className)}
          onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        >
          <Save className="size-4 shrink-0" aria-hidden />
          Save
        </Button>
      </DialogTrigger>
      <DialogContent onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}>
        <DialogHeader>
          <DialogTitle>Save to brief</DialogTitle>
          <DialogDescription>
            {profileName
              ? `Add ${profileName} to a brief for this search mandate.`
              : 'Select a brief to save this profile.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="save-brief-select">Brief</Label>
            <Select value={selectedBriefId} onValueChange={setSelectedBriefId}>
              <SelectTrigger id="save-brief-select" aria-label={label}>
                <SelectValue placeholder="Select a brief" />
              </SelectTrigger>
              <SelectContent>
                {briefs.map((brief) => (
                  <SelectItem key={brief.id} value={brief.id}>
                    {brief.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="save-brief-notes">Notes (optional)</Label>
            <Textarea
              id="save-brief-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this profile fits…"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving || briefs.length === 0}>
              {isSaving ? 'Saving…' : 'Save to brief'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
